
import React, { useState } from 'react';
import OperationsLayout from '@/components/operations/OperationsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { BookOpen, Search, Filter, Download, Plus } from 'lucide-react';

const HQSOPs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const sopCategories = [
    'all',
    'Client Care',
    'Safety & Compliance',
    'Clinical Procedures',
    'Operations',
    'Training',
    'Marketing'
  ];

  const sops = [
    {
      id: 1,
      title: "Client Intake Process",
      description: "Complete guide for welcoming new clients and gathering essential information.",
      category: "Client Care",
      lastUpdated: "2024-01-15",
      version: "2.1",
      status: "active",
      tags: ["intake", "onboarding", "client care"]
    },
    {
      id: 2,
      title: "Sanitation & Sterilization Protocol",
      description: "Detailed cleaning and sterilization procedures for all equipment and treatment rooms.",
      category: "Safety & Compliance",
      lastUpdated: "2024-01-10",
      version: "3.0",
      status: "active",
      tags: ["sanitation", "safety", "compliance"]
    },
    {
      id: 3,
      title: "Botox Injection Techniques",
      description: "Best practices for safe and effective Botox administration.",
      category: "Clinical Procedures",
      lastUpdated: "2024-01-08",
      version: "1.5",
      status: "active",
      tags: ["botox", "injections", "clinical"]
    },
    {
      id: 4,
      title: "Emergency Response Protocol",
      description: "Step-by-step procedures for handling medical emergencies and adverse reactions.",
      category: "Safety & Compliance",
      lastUpdated: "2024-01-05",
      version: "2.0",
      status: "active",
      tags: ["emergency", "safety", "response"]
    },
    {
      id: 5,
      title: "Appointment Scheduling Guidelines",
      description: "Efficient scheduling practices to optimize patient flow and staff productivity.",
      category: "Operations",
      lastUpdated: "2023-12-20",
      version: "1.3",
      status: "active",
      tags: ["scheduling", "operations", "efficiency"]
    },
    {
      id: 6,
      title: "Staff Training Requirements",
      description: "Comprehensive training program for all clinical and administrative staff.",
      category: "Training",
      lastUpdated: "2023-12-15",
      version: "4.0",
      status: "active",
      tags: ["training", "certification", "staff"]
    }
  ];

  const filteredSOPs = sops.filter(sop => {
    const matchesSearch = sop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sop.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sop.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || sop.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-[#D1F2EB] text-[#2E7D6F] border-[#2E7D6F]/20';
      case 'draft': return 'bg-[#FFF3CD] text-[#856404] border-[#856404]/20';
      case 'archived': return 'bg-[#F8F9FA] text-[#6C757D] border-[#6C757D]/20';
      default: return 'bg-[#F8F9FA] text-[#6C757D] border-[#6C757D]/20';
    }
  };

  return (
    <OperationsLayout 
      title="Standard Operating Procedures"
      subtitle="Access and manage all JUV standard operating procedures"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#A55C52]" />
              <Input
                type="text"
                placeholder="Search SOPs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-80 bg-[#FAF8F6] border-[#CDA79D] text-[#5C322B] placeholder:text-[#A55C52]/60 focus:border-[#A55C52] focus:ring-[#A55C52]/20"
              />
            </div>
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 bg-[#FAF8F6] border border-[#CDA79D] rounded-md text-[#5C322B] focus:border-[#A55C52] focus:ring-[#A55C52]/20"
            >
              {sopCategories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              className="border-[#CDA79D] text-[#5C322B] hover:bg-[#F2D9D3] hover:border-[#A55C52]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
            <Button 
              className="bg-[#A55C52] text-white hover:bg-[#5C322B] border-[#A55C52]"
            >
              <Plus className="h-4 w-4 mr-2" />
              New SOP
            </Button>
          </div>
        </div>

        {/* SOPs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSOPs.map((sop) => (
            <Card 
              key={sop.id} 
              className="bg-[#FAF8F6] border-[#CDA79D] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 rounded-2xl"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <BookOpen className="h-6 w-6 text-[#A55C52]" />
                  <Badge className={`${getStatusColor(sop.status)} rounded-full px-3 py-1 text-xs font-medium border`}>
                    {sop.status}
                  </Badge>
                </div>
                <CardTitle className="text-lg font-semibold text-[#5C322B]">
                  {sop.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[#5C322B]/80 text-sm">{sop.description}</p>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-[#A55C52]">
                    <span>Version {sop.version}</span>
                    <span>Updated {sop.lastUpdated}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {sop.tags.map((tag, index) => (
                      <Badge key={index} className="bg-[#F2D9D3] text-[#A55C52] border-[#CDA79D]/50 text-xs rounded-full px-2 py-0.5">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex space-x-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-[#CDA79D] text-[#5C322B] hover:bg-[#F2D9D3] hover:border-[#A55C52]"
                  >
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-[#CDA79D] text-[#5C322B] hover:bg-[#F2D9D3] hover:border-[#A55C52]"
                  >
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredSOPs.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-[#A55C52]/40" />
            <h3 className="mt-2 text-sm font-medium text-[#5C322B]">No SOPs found</h3>
            <p className="mt-1 text-sm text-[#5C322B]/60">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>
    </OperationsLayout>
  );
};

export default HQSOPs;
